/**
 * SEO-Komponente – setzt dynamisch <title>, <meta> und strukturierte Daten
 * pro Seite für optimale Suchmaschinen- und GEO-Sichtbarkeit.
 *
 * GEO-Keywords: Immobilienmaklerin Güglingen, Heilbronn, Ludwigsburg, Stuttgart
 */
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { REAL_ESTATE_AGENT_SCHEMA } from "@shared/real-estate-agent-schema";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  /** JSON-LD strukturierte Daten (Schema.org) als fertiges Objekt oder Array von Objekten */
  schema?: object | object[];
  /** Open Graph Bild-URL */
  ogImage?: string;
  /** Seitentyp für Open Graph */
  ogType?: "website" | "article";
  /** Zusätzliche Keywords (werden mit Basis-Keywords kombiniert) */
  keywords?: string;
  /** Personalisierte lokale Ansichten dürfen nicht in Suchergebnissen erscheinen. */
  noIndex?: boolean;
}

const BASE_KEYWORDS =
  "Immobilienmaklerin Brackenheim, Immobilienmakler Güglingen, Haus verkaufen Brackenheim, Immobilien Zabergäu, Immobilienmakler Heilbronn, Immobilienmakler Lauffen am Neckar, Immobilienmaklerin Schwaigern, Immobilienmakler Leingarten, kostenlose Immobilienbewertung";

const SITE_URL = "https://immozwerg.com";
const DEFAULT_OG_IMAGE =
  "/assets/abfc58b6e93e.webp";

export default function SEO({
  title,
  description,
  canonical,
  schema,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  keywords,
  noIndex = false,
}: SEOProps) {
  const [helmetReady, setHelmetReady] = useState(typeof window === "undefined");
  useEffect(() => setHelmetReady(true), []);

  const fullTitle = title.includes("Immozwerg") ? title : `${title} | Immozwerg`;
  const allKeywords = keywords ? `${keywords}, ${BASE_KEYWORDS}` : BASE_KEYWORDS;
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;

  if (!helmetReady) return null;

  return (
    <Helmet>
      {/* ── Basis ── */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* ── Open Graph (Facebook, WhatsApp, LinkedIn) ── */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content="Immozwerg – Andrea Heinz" />

      {/* ── Twitter Card ── */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* ── Geo-Tags ── */}
      <meta name="geo.region" content="DE-BW" />
      <meta name="geo.placename" content="Güglingen, Brackenheim, Meimsheim, Lauffen am Neckar, Kirchheim am Neckar, Schwaigern, Leingarten, Heilbronn, Böckingen" />
      <meta name="geo.position" content="49.0569;9.0097" />
      <meta name="ICBM" content="49.0569, 9.0097" />

      {/* ── Robots ── */}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />

      {/* ── Strukturierte Daten (JSON-LD) ── */}
      {schema && (Array.isArray(schema) ? schema : [schema]).map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

// ─── Vorgefertigte Schema.org-Objekte ─────────────────────────────────────────

export const schemaLocalBusiness = REAL_ESTATE_AGENT_SCHEMA;

// FAQPage-Schema wurde auf die dedizierte /faq-Seite verschoben (FAQ.tsx)
// Auf der Homepage wird kein FAQPage-Schema mehr ausgegeben.

export const schemaPerson = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Andrea Heinz",
  jobTitle: "Immobilienmaklerin",
  worksFor: {
    "@type": "Organization",
    name: "HAJ Immo e.K.",
    brand: "Immozwerg",
  },
  url: `${SITE_URL}/ueber-mich`,
  image:
    "/manus-storage/daca32472fd3_b40b8efe.png",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Lortzingstraße 5",
    postalCode: "74363",
    addressLocality: "Güglingen",
    addressRegion: "Baden-Württemberg",
    addressCountry: "DE",
  },
  telephone: "+491738660756",
  email: "hallo@haj-immokg.com",
  knowsAbout: [
    "Immobilienverkauf",
    "Kaufbegleitung",
    "Finanzierungsvermittlung",
    "Erbschaftsimmobilien",
  ],
};
